// Focused single-role editor — ONE role at a time, permissions rendered as
// grouped toggles (Operations / Pricing / Programs / Staff-Admin). Used by both
// the "edit a role" and "new role" flows in roles-tab.tsx.
//
// SERVER-AUTHORITATIVE LOCKING (architecture rule: no client-side authz):
// the set of toggles the caller may flip comes from getGrantablePermissions()
// — NEVER a hardcoded client list. A perm is LOCKED when the caller is not the
// owner AND it is not in their grantable set. A locked perm cannot be enabled
// and is excluded from the submitted permission set. Reserved perms surface an
// "Owner only" affordance.

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ApiError } from "@bw/shared/api";
import { useEffect, useMemo, useState } from "react";
import { notifications } from "@/app/notifications";
import {
  PERMISSION_GROUPS,
  PERMISSION_LABELS,
  createOrgRole,
  getGrantablePermissions,
  updateRole,
  type GrantablePermissions,
  type OrgRole,
} from "@/libs/permissionsApi";

export interface RoleEditorDialogProps {
  orgId: string;
  // The role being edited, or null when creating a new role.
  role: OrgRole | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Called after a successful save so the parent can refetch the list.
  onSaved: () => void | Promise<void>;
}

function permissionLabel(action: string): string {
  if (PERMISSION_LABELS[action]) return PERMISSION_LABELS[action];
  const suffix = action.includes(":")
    ? action.slice(action.indexOf(":") + 1)
    : action;
  const words = suffix.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : action;
}

export function RoleEditorDialog({
  orgId,
  role,
  open,
  onOpenChange,
  onSaved,
}: RoleEditorDialogProps) {
  const isCreate = role === null;

  const [name, setName] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [grantable, setGrantable] = useState<GrantablePermissions | null>(null);
  const [loadingGrantable, setLoadingGrantable] = useState(false);
  const [grantableError, setGrantableError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Seed the editor each time it opens for a (different) role / create.
  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? "");
    setSelected(new Set(role?.permissions ?? []));
    setFormError(null);
    setGrantableError(null);
    let cancelled = false;
    setLoadingGrantable(true);
    (async () => {
      try {
        const data = await getGrantablePermissions(orgId);
        if (!cancelled) setGrantable(data);
      } catch (err) {
        if (!cancelled) {
          setGrantable(null);
          setGrantableError(
            err instanceof Error ? err.message : "Failed to load permissions.",
          );
        }
      } finally {
        if (!cancelled) setLoadingGrantable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, role]);

  // A perm is locked when the caller is NOT the owner and the server did not
  // list it as grantable. Owner → nothing locked. This is the ONLY source of
  // locking — never a hardcoded client set.
  const isLocked = useMemo(() => {
    return (perm: string): boolean => {
      if (!grantable) return true; // fail closed until the server answers
      if (grantable.is_owner) return false;
      return !grantable.permissions.includes(perm);
    };
  }, [grantable]);

  const isReserved = (perm: string): boolean =>
    grantable?.reserved.includes(perm) ?? false;

  // True when at least one reserved (owner-only) permission is locked for this
  // caller — drives the single "Owner only" explanatory note at the top of the
  // toggle list (kept to ONE occurrence so it reads as a clear caption).
  const hasReservedLock = useMemo(() => {
    if (!grantable || grantable.is_owner) return false;
    return PERMISSION_GROUPS.some((g) =>
      g.permissions.some(
        (perm) =>
          grantable.reserved.includes(perm) &&
          !grantable.permissions.includes(perm),
      ),
    );
  }, [grantable]);

  // Perms the role CURRENTLY holds that this caller cannot grant (locked + ON).
  // These are preserved verbatim on save (never stripped), but the operator
  // deserves an honest heads-up that part of this role is owner-controlled and
  // will stay as the owner set it — so an edit isn't mistaken for full control.
  const heldUngrantable = useMemo(() => {
    if (!grantable || grantable.is_owner) return [] as string[];
    return Array.from(selected).filter(
      (perm) => !grantable.permissions.includes(perm),
    );
  }, [grantable, selected]);

  const toggle = (perm: string, on: boolean) => {
    if (isLocked(perm)) return; // a locked perm can never be flipped on
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(perm);
      else next.delete(perm);
      return next;
    });
  };

  const handleSave = async () => {
    if (isCreate && !name.trim()) {
      setFormError("Role name is required.");
      return;
    }
    // Submit the FULL selected set — we do NOT strip locked perms. A locked
    // toggle can't be flipped, so the actor can neither ADD a perm they can't
    // grant (locked-off stays off) nor REMOVE one (locked-on stays on). A
    // reserved perm an owner already put on the role is therefore PRESERVED in
    // the body verbatim. The backend update_role runs a DELTA guard (added ⊆
    // grantable AND removed ⊆ grantable) so an unchanged held-but-non-grantable
    // perm passes; stripping it here would silently destroy authorization.
    const permissions = Array.from(selected);

    setSaving(true);
    setFormError(null);
    try {
      if (isCreate) {
        await createOrgRole(orgId, { name: name.trim(), permissions });
      } else {
        await updateRole(role.role_id, { permissions });
      }
      await onSaved();
      onOpenChange(false);
    } catch (err) {
      // Surface the backend message honestly (escalation guard → 403). No
      // optimistic update: the list is left untouched on failure.
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not save role.";
      notifications.error(message);
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={() => {
        if (!saving) onOpenChange(false);
      }}
      open={open}
    >
      <DialogTitle>{isCreate ? "New role" : role?.name}</DialogTitle>
      <DialogContent sx={{ maxHeight: "85vh" }}>
        <DialogContentText sx={{ mb: 2.5 }}>
          Choose the permissions this role grants.
        </DialogContentText>

        <Stack spacing={2.5}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}

          {isCreate ? (
            <TextField
              disabled={saving}
              label="Role name"
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Front desk"
              value={name}
            />
          ) : null}

          {grantableError ? (
            <Alert severity="error">{grantableError}</Alert>
          ) : null}

          {loadingGrantable && !grantable ? (
            <Stack aria-label="Loading permissions" role="status" spacing={1.5}>
              {[0, 1, 2].map((i) => (
                <Skeleton height={48} key={i} variant="rounded" />
              ))}
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              {hasReservedLock ? (
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ alignItems: "center" }}
                >
                  <LockOutlinedIcon color="action" fontSize="small" />
                  <Typography color="text.secondary" variant="caption">
                    Locked toggles are <strong>Owner only</strong> — only the
                    organization owner can grant them.
                  </Typography>
                </Stack>
              ) : null}
              {heldUngrantable.length > 0 ? (
                <Alert severity="warning">
                  This role already holds an owner-only permission you
                  can&apos;t change. You can edit the toggles you control — it
                  stays as the owner set it.
                </Alert>
              ) : null}
              {PERMISSION_GROUPS.map((group) => (
                <Box
                  component="fieldset"
                  key={group.title}
                  sx={{ border: 0, m: 0, minWidth: 0, p: 0 }}
                >
                  <Typography
                    component="legend"
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 700 }}
                  >
                    {group.title}
                  </Typography>
                  <Stack spacing={0.75}>
                    {group.permissions.map((perm) => {
                      const label = permissionLabel(perm);
                      const locked = isLocked(perm);
                      const reserved = isReserved(perm);
                      const reason = reserved
                        ? "Restricted — only the organization owner can grant this."
                        : "You don't hold this permission, so you can't grant it.";
                      // WCAG 2.5.3 (label in name): the visible row text is the
                      // SAME canonical human label as the switch's accessible
                      // name (aria-label) — no divergence — so a speech-input
                      // user saying the visible words can activate the toggle.
                      return (
                        <Paper
                          data-permission-row
                          key={perm}
                          variant="outlined"
                          sx={{ px: 1.5, py: 1 }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography noWrap variant="body2">
                              {label}
                            </Typography>
                            {locked ? (
                              <Tooltip title={reason}>
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  sx={{
                                    alignItems: "center",
                                    color: "text.secondary",
                                  }}
                                >
                                  <LockOutlinedIcon fontSize="small" />
                                  <Switch
                                    checked={selected.has(perm)}
                                    disabled
                                    slotProps={{
                                      input: {
                                        "aria-describedby": `${perm}-lock`,
                                        "aria-label": label,
                                      },
                                    }}
                                  />
                                  <Box
                                    component="span"
                                    id={`${perm}-lock`}
                                    sx={{
                                      border: 0,
                                      clip: "rect(0 0 0 0)",
                                      height: "1px",
                                      m: -1,
                                      overflow: "hidden",
                                      p: 0,
                                      position: "absolute",
                                      whiteSpace: "nowrap",
                                      width: "1px",
                                    }}
                                  >
                                    {reason}
                                  </Box>
                                </Stack>
                              </Tooltip>
                            ) : (
                              <Switch
                                checked={selected.has(perm)}
                                disabled={saving}
                                onChange={(event) =>
                                  toggle(perm, event.target.checked)
                                }
                                slotProps={{ input: { "aria-label": label } }}
                              />
                            )}
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={saving}
          onClick={() => onOpenChange(false)}
          size="small"
        >
          Cancel
        </Button>
        <Button
          disabled={saving || loadingGrantable}
          onClick={() => void handleSave()}
          size="small"
          variant="contained"
        >
          {saving
            ? isCreate
              ? "Creating…"
              : "Saving…"
            : isCreate
              ? "Create role"
              : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
